<script>
  import { onMount } from "svelte";
  import Button from "lib/components/Button.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { InfoBlock, Tooltip, Select } from "paxray-components-svelte";

  const testCards = [
    {
      title: "Setup",
      description:
        "Verify the platform connection and all settings required to configure the project.",
      tests: [
        "User is reachable",
        "Framework configuration is loaded",
        "Team and user assignments are available",
        "Workshop configuration is available",
        "Blacklisting settings are applied",
      ],
    },
    {
      title: "Scoping",
      description:
        "Verify the whitelist phase and the application scope used for data collection.",
      tests: [
        "Whitelist phase can be started",
        "Whitelist phase can be completed",
        "Application names can be mapped",
        "Whitelist configuration is applied",
      ],
    },
    {
      title: "Discovery",
      description:
        "Verify task registration, anonymization, and the related progress tracking.",
      tests: [
        "Task registration can be started",
        "Anonymization check is available",
        "Number ranges can be configured",
        "Registration progress is displayed",
        "Task registration can be completed",
      ],
    },
    {
      title: "Discovery Extended",
      description:
        "Verify data collection and the preparation of recorded data for downstream use.",
      tests: [
        "Data collection can be started",
        "Activity data is captured",
        "Data collection can be completed",
        "Recorded data can be preprocessed",
      ],
    },
    {
      title: "Analysis",
      description:
        "Verify that successfully preprocessed data is available for analysis.",
      tests: [
        "Preprocessing completed successfully",
        "Analysis can be started",
        "Analysis data is available",
        "Analysis view loads correctly",
      ],
    },
  ];

  const phasePrerequisites = [
    "",
    "Complete Setup first",
    "Complete Scoping first",
    "Complete Discovery first",
    "Complete Discovery Extended first",
  ];
  let phaseStatuses = testCards.map((_, index) =>
    index === 0 ? "todo" : "blocked",
  );

  let users = [];
  let selectedUserId = "";
  let selectedUser = null;
  let userLoading = true;
  let userError = "";
  let activePhaseIndex = 0;

  $: userOptions = users.map((user) => ({
    value: user.id,
    text: user.name,
  }));

  onMount(loadSensors);

  async function loadSensors() {
    userLoading = true;
    userError = "";
    try {
      const response = await fetchWithSession(
        "/api/damprojects/getDAMTeamUsers",
      );
      if (!response.ok)
        throw new Error(`User list could not be loaded (${response.status}).`);
      const data = await response.json();
      users = (Array.isArray(data) ? data : [])
        .map((user) => {
          const rawName =
            user.name || user.fullname || user.username || String(user.id);
          const [, ...nameParts] = rawName.split("\\");
          const name = nameParts.length
            ? nameParts.join("\\").replace(/^\\+/, "")
            : rawName;
          return {
            id: user.id,
            name,
            lastupdate: user.lastupdate,
          };
        })
        .filter((user) => user.id);
    } catch (error) {
      userError = error.message || "User list could not be loaded.";
    } finally {
      userLoading = false;
    }
  }

  function selectSensor() {
    console.log({ selectedUserId });
    selectedUser =
      users.find((user) => String(user.id) === String(selectedUserId)) || null;
    activePhaseIndex = 0;
  }

  function startPhase(index) {
    if (!selectedUser || phaseStatuses[index] !== "todo") return;
    phaseStatuses[index] = "running";
    phaseStatuses = [...phaseStatuses];
    activePhaseIndex = index;
  }

  function completePhase(index) {
    if (phaseStatuses[index] !== "running") return;
    phaseStatuses[index] = "passed";
    if (index < phaseStatuses.length - 1) phaseStatuses[index + 1] = "todo";
    phaseStatuses = [...phaseStatuses];
  }

  function nextPhase(index) {
    if (phaseStatuses[index] !== "passed" || index >= testCards.length - 1)
      return;
    activePhaseIndex = index + 1;
  }
</script>

<section class="setup-test">
  <div class="developer-notices">
    <InfoBlock type="warning" heading="1. Developer sources" hideIcon>
      <div slot="message" class="developer-notice">
        <p>
          The current test UI is implemented in <code
            >src/routes/app/components/admin/AdminSetupTest.svelte</code
          >. The user list is loaded through
          <code>/api/damprojects/getDAMTeamUsers</code>
          from <code>damprojects-backend/backend/endpoints/logs.py</code>.
        </p>
      </div>
    </InfoBlock>
    <InfoBlock type="warning" heading="2. Current progress" hideIcon>
      <div slot="message" class="developer-notice">
        <ul>
          <li>Loads selectable users from the backend.</li>
          <li>
            Provides the five phases Setup, Scoping, Discovery, Discovery
            Extended, and Analysis.
          </li>
          <li>
            Displays the planned checks and enforces their phase order in the
            UI.
          </li>
          <li>
            Supports the local states To do, Running, Passed, and Blocked.
          </li>
        </ul>
      </div>
    </InfoBlock>
    <InfoBlock type="warning" heading="3. Current runtime behavior" hideIcon>
      <div slot="message" class="developer-notice">
        <p>
          Only loading the user list is a real backend operation. “Start phase
          test”, “Mark phase as passed”, and “Next phase” only update local
          component state. They do not execute or verify any platform operation.
          Reloading the page resets all phase statuses.
        </p>
      </div>
    </InfoBlock>
    <InfoBlock type="warning" heading="4. Not connected yet" hideIcon>
      <div slot="message" class="developer-notice">
        <p>
          There is no test-run API, backend job, per-check result, error detail,
          log, timeout handling, or persisted test history. Phase statuses are
          not tied to the selected user, and changing the user currently does
          not reset them.
        </p>
      </div>
    </InfoBlock>
    <InfoBlock type="warning" heading="5. Next implementation steps" hideIcon>
      <div slot="message" class="developer-notice">
        <ol>
          <li>
            Define the exact backend check and expected result for every listed
            test.
          </li>
          <li>
            Add a test-run API and execute checks for the selected user in phase
            order.
          </li>
          <li>
            Replace manual phase completion with results derived from the
            individual checks.
          </li>
          <li>
            Display per-check progress, failures, diagnostics, and timestamps.
          </li>
          <li>
            Persist test runs and add retry, timeout, authorization, and
            integration tests.
          </li>
        </ol>
      </div>
    </InfoBlock>
  </div>

  <div class="test-controls">
    <div class="sensor-selection">
      <div class="sensor-field">
        <Select
          items={userOptions}
          selected={selectedUserId}
          fitContent
          on:update={(event) => {
            selectedUserId = event.detail;
            selectSensor();
          }}
          color="dark"
          labelText="User"
          placeholder={userLoading ? "Loading..." : "No user selected"}
        />
      </div>
    </div>
    {#if userError}<p class="error">{userError}</p>{/if}
    {#if selectedUser}<p class="selected-sensor">
        Selected user: <strong>{selectedUser.name}</strong>
      </p>{/if}
  </div>

  <div class="test-card-grid">
    {#each testCards as card, index}
      <section class="test-card">
        <button
          type="button"
          class:active={activePhaseIndex === index}
          class="card-heading"
          aria-expanded={activePhaseIndex === index}
          on:click={() => (activePhaseIndex = index)}
        >
          <div class="card-title">
            <h2>{card.title}</h2>
            <Tooltip text={card.description} direction="right" align="center" />
          </div>
          <div class="card-status-group">
            {#if phaseStatuses[index] === "blocked"}
              <span class="card-hint">{phasePrerequisites[index]}</span>
            {/if}
            <span
              class:blocked={phaseStatuses[index] === "blocked"}
              class:passed={phaseStatuses[index] === "passed"}
              class:running={phaseStatuses[index] === "running"}
              class="card-status"
            >
              {phaseStatuses[index] === "todo"
                ? "To do"
                : phaseStatuses[index] === "running"
                  ? "Running"
                  : phaseStatuses[index] === "passed"
                    ? "Passed"
                    : "Blocked"}
            </span>
          </div>
        </button>
        {#if activePhaseIndex === index}
          <div class="test-list">
            {#each card.tests as test}
              <div
                class:blocked={phaseStatuses[index] === "blocked"}
                class="test-row"
                aria-disabled={phaseStatuses[index] === "blocked"}
              >
                <span class="check-icon" aria-hidden="true">–</span>
                <span>{test}</span>
              </div>
            {/each}
          </div>
          <div class="phase-actions">
            {#if phaseStatuses[index] === "passed" && index < testCards.length - 1}
              <Button
                text="Next phase"
                variant="secondary"
                on:click={() => nextPhase(index)}
              />
            {:else if phaseStatuses[index] === "running"}
              <Button
                text="Mark phase as passed"
                variant="secondary"
                on:click={() => completePhase(index)}
              />
            {:else}
              <span
                class="phase-action-with-tooltip"
                title={!selectedUser
                  ? "Select a user first"
                  : phasePrerequisites[index]}
              >
                <Button
                  text="Start phase test"
                  variant="secondary"
                  disabled={!selectedUser || phaseStatuses[index] !== "todo"}
                  on:click={() => startPhase(index)}
                />
              </span>
            {/if}
          </div>
        {/if}
      </section>
    {/each}
  </div>
</section>

<style>
  .setup-test {
    display: grid;
    gap: 1rem;
  }
  .developer-notices {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    align-items: stretch;
  }
  .developer-notices :global(.warning-notice) {
    margin-bottom: 0;
  }
  .developer-notice {
    line-height: 1.35;
  }
  .developer-notice p,
  .developer-notice ul,
  .developer-notice ol {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.35;
  }
  .developer-notice ul,
  .developer-notice ol {
    padding-left: 1.1rem;
  }
  .developer-notice ul {
    list-style: disc;
  }
  .developer-notice ol {
    list-style: decimal;
  }
  .developer-notice li {
    display: list-item;
    margin-top: 0.125rem;
  }
  .developer-notice code {
    overflow-wrap: anywhere;
    font-size: 0.75rem;
  }
  .test-controls {
    display: grid;
    gap: var(--control-gap);
  }
  .selected-sensor,
  .error {
    margin-top: 0.3rem;
    color: #6f7681;
  }
  .sensor-selection {
    display: flex;
    flex-wrap: wrap;
    gap: var(--control-gap);
  }
  .sensor-field {
    display: flex;
    width: min(100%, 24rem);
    flex-direction: column;
    gap: 0.2rem;
    font-size: 0.75rem;
  }
  .selected-sensor {
    margin-bottom: 0;
  }
  .selected-sensor strong {
    color: var(--pax-black);
  }
  .error {
    margin-bottom: 0;
    color: #750e13;
  }
  .test-card-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }
  .test-card {
    overflow: hidden;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background: var(--pax-white);
  }
  .card-heading {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-height: 3rem;
    padding: 0.75rem 1rem;
    border: 0;
    background: var(--pax-black);
    color: var(--pax-white);
    text-align: left;
    cursor: pointer;
  }
  .card-heading:not(.active) {
    opacity: 0.78;
  }
  .card-heading.active {
    opacity: 1;
  }
  .card-title {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 1rem;
  }
  .card-status-group {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
  }
  .card-heading h2 {
    margin: 0;
    color: inherit;
    font-family: Inter;
    font-size: 1rem;
    font-style: normal;
    font-weight: 400;
    line-height: 100%;
  }
  .card-status {
    padding: 0.2rem 0.5rem;
    border: 1px solid #f1c21b;
    border-radius: 1rem;
    color: #f1c21b;
    font-size: 0.75rem;
  }
  .card-status.blocked {
    border-color: #8d8d8d;
    color: #c6c6c6;
  }
  .card-status.running {
    border-color: #4589ff;
    color: #a6c8ff;
  }
  .card-status.passed {
    border-color: #42be65;
    color: #a7f0ba;
  }
  .card-hint {
    color: #f1c21b;
    font-size: 0.75rem;
  }
  .test-list {
    border-top: 0;
  }
  .test-row {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.7rem 1rem;
    border-bottom: 1px solid rgba(28, 28, 32, 0.12);
    color: var(--pax-black);
    font-size: 0.85rem;
  }
  .test-row:last-child {
    border-bottom: 0;
  }
  .test-row.blocked {
    color: #8d8d8d;
  }
  .test-row.blocked .check-icon {
    border-color: #bdbdbd;
    color: #bdbdbd;
  }
  .phase-actions {
    display: flex;
    justify-content: flex-start;
    padding: 0.75rem 1rem 1rem;
  }
  .phase-action-with-tooltip {
    display: inline-flex;
    align-items: center;
  }
  .check-icon {
    display: inline-flex;
    width: 1.15rem;
    height: 1.15rem;
    align-items: center;
    justify-content: center;
    border: 1px solid #8d8d8d;
    border-radius: 50%;
    color: #8d8d8d;
    font-weight: 700;
  }
  @media (max-width: 42rem) {
    .developer-notices {
      grid-template-columns: 1fr;
    }
    .sensor-selection {
      align-items: stretch;
      flex-direction: column;
    }
    .sensor-field {
      width: 100%;
    }
  }
</style>
